import { useEffect, useState } from 'react';
import {
  collection, getDocs, writeBatch, addDoc, doc, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const COLLECTIONS = [
  {
    id: 'requests',
    label: 'Requests',
    description: 'All tour requests, quotes and messages',
    subcollection: 'messages',
    color: 'blue',
  },
  {
    id: 'invoices',
    label: 'Invoices',
    description: 'All invoices issued',
    color: 'amber',
  },
  {
    id: 'users',
    label: 'Users',
    description: 'All user accounts except your own admin account',
    color: 'purple',
    note: 'Deletes Firestore records only — Firebase Auth accounts must be removed separately (see below)',
  },
  {
    id: 'emailLogs',
    label: 'Email Logs',
    description: 'All email send records',
    color: 'gray',
  },
  {
    id: 'bookingSuppliers',
    label: 'Supplier Assignments',
    description: 'All supplier-to-request assignments',
    color: 'teal',
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'All uploaded document records',
    color: 'green',
  },
];

const COLOR_STYLES = {
  blue:   'bg-blue-light border-pb/20 text-pb',
  amber:  'bg-amber-light border-ba/20 text-[#92400E]',
  purple: 'bg-[#F5F3FF] border-[#8B5CF6]/20 text-[#5B21B6]',
  gray:   'bg-[#F3F4F6] border-[#E5E7EB] text-[#374151]',
  teal:   'bg-[#CCFBF1] border-bt/20 text-[#0F766E]',
  green:  'bg-green-light border-sg/20 text-[#065F46]',
};

async function deleteInBatches(refs) {
  for (let i = 0; i < refs.length; i += 400) {
    const batch = writeBatch(db);
    refs.slice(i, i + 400).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
}

export default function DataCleanup() {
  const { currentUser, isAdmin } = useAuth();
  const [counts, setCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [deleted, setDeleted] = useState({});
  const [errors, setErrors] = useState({});
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [archivedBackfillRunning, setArchivedBackfillRunning] = useState(false);
  const [archivedBackfillResult, setArchivedBackfillResult] = useState(null);

  async function loadCounts() {
    setLoadingCounts(true);
    const results = {};
    await Promise.all(
      COLLECTIONS.map(async (col) => {
        try {
          const snap = await getDocs(collection(db, col.id));
          results[col.id] = snap.size;
        } catch {
          results[col.id] = '—';
        }
      })
    );
    setCounts(results);
    setLoadingCounts(false);
  }

  useEffect(() => { loadCounts(); }, []);

  async function deleteCollection(colId) {
    setDeleting(colId);
    setErrors((prev) => ({ ...prev, [colId]: null }));
    try {
      const colRef = collection(db, colId);
      const snap = await getDocs(colRef);
      const config = COLLECTIONS.find((c) => c.id === colId);

      if (config?.subcollection) {
        for (const docSnap of snap.docs) {
          const subSnap = await getDocs(collection(db, colId, docSnap.id, config.subcollection));
          const subRefs = subSnap.docs.map((d) => d.ref);
          if (subRefs.length) await deleteInBatches(subRefs);
        }
      }

      let refs = snap.docs.map((d) => d.ref);
      if (colId === 'users') {
        refs = refs.filter((r) => r.id !== currentUser?.uid);
      }

      await deleteInBatches(refs);
      setDeleted((prev) => ({ ...prev, [colId]: true }));
      await loadCounts();
    } catch (err) {
      console.error(`Failed to delete ${colId}:`, err);
      setErrors((prev) => ({ ...prev, [colId]: err.message }));
    } finally {
      setDeleting(null);
    }
  }

  async function runTourSummaryBackfill() {
    setBackfillRunning(true);
    setBackfillResult(null);
    try {
      const taskRef = await addDoc(collection(db, 'adminTasks'), {
        type: 'backfill_tour_summaries',
        requestedBy: currentUser.uid,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      const unsub = onSnapshot(doc(db, 'adminTasks', taskRef.id), (snap) => {
        const data = snap.data();
        if (!data || data.status === 'pending') return;
        setBackfillResult(data);
        setBackfillRunning(false);
        unsub();
      });
    } catch (err) {
      console.error('Tour summary backfill failed:', err);
      setBackfillResult({ status: 'denied', error: err.message });
      setBackfillRunning(false);
    }
  }

  async function runArchivedBackfill() {
    setArchivedBackfillRunning(true);
    setArchivedBackfillResult(null);
    try {
      const taskRef = await addDoc(collection(db, 'adminTasks'), {
        type: 'backfill_archived_flag',
        requestedBy: currentUser.uid,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      const unsub = onSnapshot(doc(db, 'adminTasks', taskRef.id), (snap) => {
        const data = snap.data();
        if (!data || data.status === 'pending') return;
        setArchivedBackfillResult(data);
        setArchivedBackfillRunning(false);
        unsub();
      });
    } catch (err) {
      console.error('Archived-flag backfill failed:', err);
      setArchivedBackfillResult({ status: 'denied', error: err.message });
      setArchivedBackfillRunning(false);
    }
  }

  async function deleteAll() {
    setDeleting('all');
    for (const col of COLLECTIONS) {
      await deleteCollection(col.id);
    }
    setDeleting(null);
    setConfirmText('');
  }

  if (!isAdmin) {
    return (
      <div className="bg-red-light border border-dr/25 text-dr rounded-[12px] p-6 text-[13px]">
        This page is restricted to admins only.
      </div>
    );
  }

  const confirmReady = confirmText.trim() === 'DELETE ALL';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-[20px] font-bold text-[#111827]">Data Cleanup</h2>
        <p className="text-[13px] text-[#6B7280] mt-1">
          Remove test data before going live. This is permanent and cannot be undone.
        </p>
      </div>

      {/* Tour summary backfill — safe, additive, not destructive */}
      <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[#111827] mb-1">Backfill tour summaries</h3>
        <p className="text-[13px] text-[#6B7280] mb-4">
          One-time sync that populates the minimal tour list (name + dates) the <strong>marketing</strong> role
          reads from. New and updated tours sync automatically — run this once for tours that already existed
          before that feature was added.
        </p>
        <button
          onClick={runTourSummaryBackfill}
          disabled={backfillRunning}
          className="text-[13px] font-semibold text-pb hover:text-navy border border-pb/30 hover:border-pb/50 px-[14px] py-[8px] rounded-[8px] transition disabled:opacity-40"
        >
          {backfillRunning ? 'Running…' : 'Run backfill'}
        </button>
        {backfillResult && (
          backfillResult.status === 'done' ? (
            <p className="text-[12px] text-sg mt-2">
              Done — {backfillResult.success} synced{backfillResult.failed ? `, ${backfillResult.failed} failed` : ''}.
            </p>
          ) : (
            <p className="text-[12px] text-dr mt-2">Failed: {backfillResult.error || 'Unknown error'}</p>
          )
        )}
      </div>

      {/* Archived-flag backfill — safe, additive, not destructive */}
      <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[#111827] mb-1">Backfill archived flag</h3>
        <p className="text-[13px] text-[#6B7280] mb-4">
          One-time migration that stamps <code>archived: false</code> on existing tours so they appear in the
          Tours list. New tours set this automatically — run this once for tours created before the archive
          feature was added.
        </p>
        <button
          onClick={runArchivedBackfill}
          disabled={archivedBackfillRunning}
          className="text-[13px] font-semibold text-pb hover:text-navy border border-pb/30 hover:border-pb/50 px-[14px] py-[8px] rounded-[8px] transition disabled:opacity-40"
        >
          {archivedBackfillRunning ? 'Running…' : 'Run backfill'}
        </button>
        {archivedBackfillResult && (
          archivedBackfillResult.status === 'done' ? (
            <p className="text-[12px] text-sg mt-2">
              Done — {archivedBackfillResult.success} updated{archivedBackfillResult.failed ? `, ${archivedBackfillResult.failed} failed` : ''}.
            </p>
          ) : (
            <p className="text-[12px] text-dr mt-2">Failed: {archivedBackfillResult.error || 'Unknown error'}</p>
          )
        )}
      </div>

      {/* Warning banner */}
      <div className="bg-red-light border border-dr/25 rounded-[12px] p-5">
        <p className="text-[13px] font-semibold text-dr mb-1">This action is irreversible</p>
        <p className="text-[13px] text-dr/80">
          Deleted records cannot be recovered. Only use this to wipe test data before the portal goes live.
          Your own admin account will not be deleted.
        </p>
      </div>

      {/* Per-collection cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {COLLECTIONS.map((col) => (
          <div key={col.id} className="bg-white border border-[#E5E7EB] rounded-[12px] p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-[13px] font-semibold text-[#111827]">{col.label}</p>
                  <span className={`text-[11px] font-bold px-[7px] py-[2px] rounded-[5px] border ${COLOR_STYLES[col.color]}`}>
                    {loadingCounts ? '…' : counts[col.id] ?? 0}
                  </span>
                  {deleted[col.id] && (
                    <span className="text-[12px] text-sg font-medium">Cleared</span>
                  )}
                </div>
                <p className="text-[12px] text-[#6B7280]">{col.description}</p>
                {col.note && (
                  <p className="text-[12px] text-ba mt-1">{col.note}</p>
                )}
              </div>
            </div>
            {errors[col.id] && (
              <p className="text-[12px] text-dr mb-2">Error: {errors[col.id]}</p>
            )}
            <button
              onClick={() => deleteCollection(col.id)}
              disabled={deleting !== null || counts[col.id] === 0 || counts[col.id] === '—'}
              className="text-[12px] font-semibold text-dr hover:text-dr/80 border border-dr/30 hover:border-dr/50 px-[12px] py-[6px] rounded-[8px] transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting === col.id ? 'Deleting…' : `Delete all ${col.label.toLowerCase()}`}
            </button>
          </div>
        ))}
      </div>

      {/* Delete everything */}
      <div className="bg-white border border-dr/20 rounded-[12px] p-6">
        <h3 className="text-[14px] font-semibold text-[#111827] mb-1">Delete everything at once</h3>
        <p className="text-[13px] text-[#6B7280] mb-4">
          Clears all collections above in one go. Type <strong className="font-mono text-[#374151]">DELETE ALL</strong> to confirm.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE ALL to confirm"
            className="border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-dr w-56"
          />
          <button
            onClick={deleteAll}
            disabled={!confirmReady || deleting !== null}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-dr hover:bg-dr/90 rounded-[8px] transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {deleting === 'all' ? 'Deleting…' : 'Delete all test data'}
          </button>
        </div>
      </div>

      {/* Firebase Auth cleanup guidance */}
      <div className="bg-amber-light border border-ba/25 rounded-[12px] p-5">
        <p className="text-[13px] font-semibold text-[#92400E] mb-2">Firebase Auth accounts — manual step required</p>
        <p className="text-[13px] text-[#92400E]/90 mb-3">
          Deleting a user above removes their portal record but leaves their Firebase Auth account intact.
          To fully remove test users you need to delete them from the Firebase Console:
        </p>
        <ol className="text-[13px] text-[#92400E]/90 space-y-1 list-decimal list-inside">
          <li>Go to <strong>console.firebase.google.com</strong> → select your project</li>
          <li>Navigate to <strong>Authentication → Users</strong></li>
          <li>Select the test accounts and click <strong>Delete account</strong></li>
        </ol>
      </div>
    </div>
  );
}
